import React, { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { atoms } from '@fiverr-private/theme';
import { I18n } from '@fiverr-private/i18n-react';
import { Container } from '@fiverr-private/layout_components';
import { Heading, Link } from '@fiverr-private/typography';
import { PencilIcon } from '@fiverr-private/icons';
import { IconButton } from '@fiverr-private/button';
import { useIntersectionObserver } from '@fiverr-private/hooks';
import { logger } from '@fiverr-private/obs';
import { colorGrey900 } from '@fiverr-private/sass/helpers';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { useMonitoring } from '../../../../utils/monitoring';
import { useSellerData } from '../../../../hooks/useSellerData';
import { useIsSelfViewFreelancer } from '../../../../hooks/useIsSelfViewFreelancer';
import { FederatedSeller } from '../../../../types';
import workExperienceBIEvents from '../../utils/biEvents';
import { EXPERIENCES_TO_SHOW, getWorkExperienceAmount, T_KEY } from '../../utils';
import WorkExperienceList from '../WorkExperienceList';

import { iconContainer } from '../../shared/styles.ve.css';

const heading = atoms({
    display: 'flex',
    alignItems: 'center',
});

const onRenderError = (originalError: any) => {
    const err = new Error('Failed while rendering the Work Experience Component');
    logger.error(err, {
        cause: originalError,
    });
};

interface WorkExperienceProps {
    titleClassName?: string;
    setIsDrawerOpen: (isDrawerOpen: boolean) => void;
}

export const WorkExperience: FC<WorkExperienceProps> = ({ titleClassName, setIsDrawerOpen }) => {
    const isSelfView = useIsSelfViewFreelancer();
    const { Providers, fireEvents, fireImpressionEvents } = useMonitoring();
    const { workExperiences, sellerId } = useSellerData(selectSellerSlice);
    const workExperienceAmount = getWorkExperienceAmount(workExperiences);
    const [shouldShowAll, setShouldShowAll] = useState(false);

    const observableRef = useRef<HTMLElement>(null);
    const entry = useIntersectionObserver(observableRef, { threshold: 1 });
    const isVisible = Boolean(entry?.isIntersecting);

    const onSeeAllBI = () => {
        fireEvents({
            [Providers.Mixpanel]: {
                type: 'clicked work experience see all experiences',
            },
            [Providers.BigQuery]: workExperienceBIEvents.clickOnSeeAllExperiences(),
        });
    };

    const seeAllClickHandler = () => {
        onSeeAllBI();
        setShouldShowAll(true);
    };

    useEffect(() => {
        if (isVisible) {
            fireImpressionEvents({
                [Providers.Mixpanel]: {
                    type: 'Work experience - Viewed all',
                },
                [Providers.BigQuery]: workExperienceBIEvents.impressionOnAllExperiences({
                    sellerId,
                    workExperienceAmount,
                    workExperienceTotal: workExperiences?.length,
                    workExperiences,
                }),
            });
        }
    }, [
        isVisible,
        Providers.Mixpanel,
        Providers.BigQuery,
        fireImpressionEvents,
        workExperienceAmount,
        workExperiences?.length,
        sellerId,
        workExperiences,
    ]);

    return (
        <ErrorBoundary onError={onRenderError}>
            {!workExperiences?.length ? null : (
                <Container ref={observableRef} maxWidth="600px">
                    <Heading as="h4" marginBottom={{ default: '6' }} className={classNames(heading, titleClassName)}>
                        <I18n k={`${T_KEY}.title`} />
                        {isSelfView && (
                            <IconButton
                                size="xs"
                                variant="ghost"
                                aria-label="edit-work-experience"
                                className={iconContainer}
                                onClick={() => setIsDrawerOpen(true)}
                            >
                                <PencilIcon color={colorGrey900} />
                            </IconButton>
                        )}
                    </Heading>
                    <WorkExperienceList showAll={shouldShowAll} />
                    {workExperiences.length > EXPERIENCES_TO_SHOW && !shouldShowAll && (
                        <Link onClick={seeAllClickHandler} color="grey_1100" cursor="pointer" fontWeight="semibold">
                            <I18n k={`${T_KEY}.show_more`} params={{ count: workExperiences.length }} />
                        </Link>
                    )}
                </Container>
            )}
        </ErrorBoundary>
    );
};

function selectSellerSlice(seller: FederatedSeller) {
    return {
        sellerId: seller.user.id,
        workExperiences: seller.workExperiences.nodes,
    };
}
